import { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { authService } from '@/services/authService';
import { useToast } from '@/hooks/use-toast';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Récupérer la session au chargement
    const getInitialSession = async () => {
      setLoading(true);
      try {
        const { session, error } = await authService.getSession();
        if (error) throw error;
        
        setSession(session);
        if (session) {
          const { user, error: userError } = await authService.getCurrentUser();
          if (userError) throw userError;
          setUser(user);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de la session:', error);
        toast({
          title: 'Erreur de session',
          description: 'Impossible de récupérer votre session',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Configurer l'écouteur d'événements pour les changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        if (session) {
          const { user } = await authService.getCurrentUser();
          setUser(user);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [toast]);

  // Connexion avec email et mot de passe
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const { user, session, error } = await authService.signInWithEmail(email, password);
    setLoading(false);
    
    if (error) {
      toast({
        title: 'Erreur de connexion',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error };
    }
    
    setUser(user);
    setSession(session);
    toast({
      title: 'Connexion réussie',
      description: 'Vous êtes maintenant connecté',
    });
    return { success: true, error: null };
  };

  // Connexion avec code à usage unique
  const signInWithOTP = async (email: string) => {
    setLoading(true);
    const { error } = await authService.signInWithOTP(email);
    setLoading(false);
    
    if (error) {
      toast({
        title: 'Erreur d\'envoi du code',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error };
    }
    
    toast({
      title: 'Code envoyé',
      description: 'Vérifiez votre email pour le code de connexion',
    });
    return { success: true, error: null };
  };

  // Déconnexion
  const signOut = async () => {
    setLoading(true);
    const { error } = await authService.signOut();
    setLoading(false);
    
    if (error) {
      toast({
        title: 'Erreur de déconnexion',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error };
    }
    
    setUser(null);
    setSession(null);
    toast({
      title: 'Déconnexion réussie',
      description: 'Vous êtes maintenant déconnecté',
    });
    return { success: true, error: null };
  };

  return {
    user,
    session,
    loading,
    signIn,
    signInWithOTP,
    signOut,
    isAuthenticated: !!user,
  };
};

// Importation nécessaire pour l'écouteur d'événements
import { supabase } from '@/lib/supabase';